import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Upload } from 'lucide-react';
import {
  LegacyRatesImportError,
  downloadLegacyExportQuery,
  downloadLegacyRatesSample,
  getLegacyRatesImportLog,
  importLegacyRates,
} from '../../api/legacyRatesImport';
import { setTenantId } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit as canEditUtil } from '../../utils/permissions';
import type {
  LegacyRateImportLogRow,
  LegacyRatesImportResult,
  LegacyRatesIssue,
  LegacyRatesResultRow,
} from '../../types/LegacyRatesImport';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import './LegacyRatesImportPage.css';
import { formatDateTimeIL } from '../../utils/dateTime';

const RESOURCE = 'legacyRatesImport';
const GUTTER_BUFFER = 20; // extra numbers rendered above/below the visible window
const LINE_TYPES = ['R', 'H', 'M', 'N', 'C', 'S'] as const;
type LineType = (typeof LINE_TYPES)[number];

const typeLabel = (lineType: string): string =>
  (LINE_TYPES as readonly string[]).includes(lineType)
    ? t(`legacyRatesImport.types.${lineType as LineType}`)
    : lineType;

function fmtWhen(iso: string): string {
  return formatDateTimeIL(iso) || iso;
}

function compactResult(json: string | null): string {
  if (!json) return '';
  try {
    const rows = JSON.parse(json) as LegacyRatesResultRow[];
    return rows
      .map((r) =>
        t('legacyRatesImport.log.compactItem', {
          type: r.lineType,
          inserted: r.inserted,
          updated: r.updated,
          skipped: r.skipped,
        }),
      )
      .join('  ');
  } catch {
    return '';
  }
}

export function LegacyRatesImportPage() {
  const { user } = useAuth();
  const tenantId = user?.currentTenant?.tenantId ?? null;
  const canEdit = canEditUtil(user, RESOURCE);

  // The paste box is UNCONTROLLED: a pasted dump can be ~2MB, and a controlled value would
  // re-render (and diff) it on every page render. The text lives in the DOM node; state only
  // carries what the UI needs (enable flag + line count).
  const textRef = useRef<HTMLTextAreaElement>(null);
  const gutterRef = useRef<HTMLPreElement>(null);
  const gutterWrapRef = useRef<HTMLDivElement>(null);
  const gutterLinesRef = useRef(1);
  const gutterKeyRef = useRef('');
  const gutterTimerRef = useRef<number | undefined>(undefined);
  const [hasText, setHasText] = useState(false);
  const [lineCount, setLineCount] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<LegacyRatesImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<LegacyRatesIssue[]>([]);
  const [log, setLog] = useState<LegacyRateImportLogRow[]>([]);
  const [logError, setLogError] = useState(false);

  const loadLog = useCallback(async () => {
    try {
      setLog(await getLegacyRatesImportLog());
      setLogError(false);
    } catch {
      setLogError(true);
    }
  }, []);

  useEffect(() => {
    if (tenantId === null) return;
    setTenantId(tenantId);
    void loadLog();
  }, [tenantId, loadLog]);

  // Line-number gutter. N = newline count + 1, which is exactly how the server's parser numbers
  // lines (split on \n after normalising \r\n, 1-based, blank lines and a leading "line" header
  // row included), so "line N" in the issues table matches the gutter. The numbers are WINDOWED:
  // only the visible lines (+ a buffer) are in the DOM, positioned by transform from the
  // textarea's scrollTop. A full 40K-line <pre> made the first scroll cost ~0.5s of layout.
  const drawGutter = useCallback(() => {
    const ta = textRef.current;
    const pre = gutterRef.current;
    if (!ta || !pre) return;
    const cs = getComputedStyle(ta);
    const lh = parseFloat(cs.lineHeight) || 18;
    const pad = parseFloat(cs.paddingTop) || 0;
    const n = gutterLinesRef.current;
    const st = ta.scrollTop;
    const first = Math.max(0, Math.floor((st - pad) / lh) - GUTTER_BUFFER);
    const count = Math.max(0, Math.min(n - first, Math.ceil(ta.clientHeight / lh) + 2 * GUTTER_BUFFER + 2));
    const key = `${first}:${count}`;
    if (gutterKeyRef.current !== key) {
      gutterKeyRef.current = key;
      const parts = new Array<number>(count);
      for (let i = 0; i < count; i++) parts[i] = first + i + 1;
      pre.textContent = parts.join('\n');
    }
    pre.style.transform = `translateY(${pad + first * lh - st}px)`;
  }, []);

  const renderGutter = useCallback(() => {
    const v = textRef.current?.value ?? '';
    let n = 1;
    for (let i = v.indexOf('\n'); i !== -1; i = v.indexOf('\n', i + 1)) n++;
    if (n !== gutterLinesRef.current) {
      gutterLinesRef.current = n;
      gutterKeyRef.current = '';
      gutterWrapRef.current?.style.setProperty('--gutter-digits', String(String(n).length));
    }
    drawGutter();
  }, [drawGutter]);

  useEffect(() => {
    renderGutter();
    const ta = textRef.current;
    let raf = 0;
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0;
          drawGutter();
        });
    };
    ta?.addEventListener('scroll', onScroll, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' && ta ? new ResizeObserver(onScroll) : null;
    if (ta) ro?.observe(ta);
    return () => {
      ta?.removeEventListener('scroll', onScroll);
      ro?.disconnect();
      if (raf) cancelAnimationFrame(raf);
      window.clearTimeout(gutterTimerRef.current);
    };
  }, [renderGutter, drawGutter]);

  const pickFile = (files: File[]) => {
    setFile(files[0] ?? null);
    setResult(null);
    setError(null);
    setIssues([]);
  };

  const onTextInput = () => {
    window.clearTimeout(gutterTimerRef.current);
    gutterTimerRef.current = window.setTimeout(renderGutter, 150);
    const v = textRef.current?.value ?? '';
    const nonBlank = /\S/.test(v);
    let lines = 0;
    if (nonBlank) {
      lines = 1;
      for (let i = v.indexOf('\n'); i !== -1; i = v.indexOf('\n', i + 1)) lines++;
    }
    setHasText((prev) => (prev === nonBlank ? prev : nonBlank));
    setLineCount((prev) => (prev === lines ? prev : lines));
    setResult(null);
    setError(null);
    setIssues([]);
  };

  const hasInput = file !== null || hasText;

  const { run, running } = useAsyncAction(async () => {
    const text = textRef.current?.value ?? '';
    if (file === null && !/\S/.test(text)) return;
    setError(null);
    setIssues([]);
    setResult(null);
    try {
      setResult(await importLegacyRates({ file, text }));
      void loadLog();
    } catch (e) {
      if (e instanceof LegacyRatesImportError) {
        setError(e.message);
        setIssues(e.issues);
      } else {
        setError(t('legacyRatesImport.genericError'));
      }
    }
  }, { paintFirst: true });

  const download = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch {
      setError(t('legacyRatesImport.downloadError'));
    }
  };

  const d = result?.legacyDates;
  const dateItems: Array<[string, string | number | null]> = d
    ? [
        [t('legacyRatesImport.dates.effectiveOpenDate'), d.effectiveOpenDate],
        [t('legacyRatesImport.dates.zarimShiaruchDate'), d.zarimShiaruchDate],
        [t('legacyRatesImport.dates.fxDate4Israeli'), d.fxDate4Israeli],
        [t('legacyRatesImport.dates.prevBusinessDate'), d.prevBusinessDate],
        [t('legacyRatesImport.dates.bursaRegilaDate'), d.bursaRegilaDate],
        [t('legacyRatesImport.dates.bursaMakdimaDate'), d.bursaMakdimaDate],
        [t('legacyRatesImport.dates.typeIsr'), d.typeIsr],
        [t('legacyRatesImport.dates.typeZar'), d.typeZar],
      ]
    : [];

  return (
    <div className="legacy-rates-page">
      <h1>{t('legacyRatesImport.pageTitle')}</h1>
      <p className="legacy-rates-page__info">{t('legacyRatesImport.explanation')}</p>
      <div className="legacy-rates-links">
        <LopButton
          id="legacy-rates-query-link"
          variant="link"
          icon={<Download size={14} />}
          onClick={() => download(downloadLegacyExportQuery)}
        >
          {t('legacyRatesImport.exportQuery')}
        </LopButton>
        <LopButton
          id="legacy-rates-sample-link"
          variant="link"
          icon={<Download size={14} />}
          onClick={() => download(downloadLegacyRatesSample)}
        >
          {t('legacyRatesImport.sample')}
        </LopButton>
      </div>

      <div className="legacy-rates-editor" ref={gutterWrapRef}>
        <div className="legacy-rates-gutter" aria-hidden="true" id="legacy-rates-gutter">
          <pre ref={gutterRef} className="legacy-rates-gutter__nums" />
        </div>
        <textarea
          id="legacy-rates-text"
          ref={textRef}
          className="legacy-rates-textarea"
          wrap="off"
          spellCheck={false}
          placeholder={t('legacyRatesImport.textPlaceholder')}
          aria-label={t('legacyRatesImport.textLabel')}
          disabled={file !== null || !canEdit}
          onInput={onTextInput}
        />
      </div>
      {hasText && (
        <p className="legacy-rates-note" id="legacy-rates-linecount">
          {t('legacyRatesImport.lineCount', { count: lineCount.toLocaleString('he-IL') })}
        </p>
      )}
      <LopFilePicker
        id="legacy-rates-file"
        accept=".txt,.csv,.tsv,.zip"
        files={file ? [file] : []}
        onChange={pickFile}
        disabled={!canEdit}
        title={t('legacyRatesImport.chooseFile')}
        hint={t('legacyRatesImport.dropHint')}
        replaceHint={t('legacyRatesImport.replaceFile')}
        clearLabel={t('legacyRatesImport.clearFile')}
      />
      <div className="legacy-rates-actions">
        {canEdit && (
          <LopButton
            id="legacy-rates-run"
            variant="primary"
            icon={<Upload size={14} />}
            disabled={!hasInput}
            loading={running}
            onClick={() => run()}
          >
            {t('legacyRatesImport.run')}
          </LopButton>
        )}
      </div>

      {error && (
        <div className="legacy-rates-error" role="alert" id="legacy-rates-error">
          {error}
        </div>
      )}
      {issues.length > 0 && (
        <div className="legacy-rates-table-wrap legacy-rates-issues" id="legacy-rates-issues">
          <table className="legacy-rates-table">
            <thead>
              <tr>
                <th>{t('legacyRatesImport.issues.line')}</th>
                <th>{t('legacyRatesImport.issues.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((i, idx) => (
                <tr key={`${i.line}-${idx}`}>
                  <td className="num">{i.line}</td>
                  <td className="wrap">{i.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && (
        <section id="legacy-rates-result">
          <h2 className="legacy-rates-section-title">{t('legacyRatesImport.resultTitle')}</h2>
          <dl className="legacy-rates-dates">
            {dateItems.map(([label, value]) => (
              <div key={label}>
                <dt>{label}:</dt> <dd>{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
          {result.warnings.map((w) => (
            <div key={w} className="legacy-rates-warning" role="status">
              {w}
            </div>
          ))}
          <div className="legacy-rates-table-wrap">
            <table className="legacy-rates-table">
              <thead>
                <tr>
                  <th>{t('legacyRatesImport.cols.type')}</th>
                  <th>{t('legacyRatesImport.cols.received')}</th>
                  <th>{t('legacyRatesImport.cols.inserted')}</th>
                  <th>{t('legacyRatesImport.cols.updated')}</th>
                  <th>{t('legacyRatesImport.cols.skipped')}</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((r) => (
                  <tr key={r.lineType}>
                    <td>{typeLabel(r.lineType)}</td>
                    <td className="num">{r.received}</td>
                    <td className="num">{r.inserted}</td>
                    <td className="num">{r.updated}</td>
                    <td className="num">{r.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="legacy-rates-note">
            {t('legacyRatesImport.comparisonLines')}: {result.comparisonLines}
          </p>
        </section>
      )}

      <section>
        <h2 className="legacy-rates-section-title">{t('legacyRatesImport.log.title')}</h2>
        {logError && <div className="legacy-rates-error">{t('legacyRatesImport.log.error')}</div>}
        <div className="legacy-rates-table-wrap" id="legacy-rates-log">
          <table className="legacy-rates-table">
            <thead>
              <tr>
                <th>{t('legacyRatesImport.log.when')}</th>
                <th>{t('legacyRatesImport.log.by')}</th>
                <th>{t('legacyRatesImport.log.source')}</th>
                <th>{t('legacyRatesImport.log.lines')}</th>
                <th>{t('legacyRatesImport.log.legacyDates')}</th>
                <th>{t('legacyRatesImport.log.result')}</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 && (
                <tr>
                  <td colSpan={6} className="legacy-rates-note">
                    {t('legacyRatesImport.log.empty')}
                  </td>
                </tr>
              )}
              {log.map((r) => (
                <tr key={r.importId}>
                  <td>{fmtWhen(r.importedAt)}</td>
                  <td>{r.importedBy}</td>
                  <td>{r.sourceName}</td>
                  <td className="num">{r.lineCount}</td>
                  <td className="legacy-rates-ltr">
                    {[r.effectiveOpenDate, r.zarimShiaruchDate].filter(Boolean).join(' / ')}
                  </td>
                  <td className="legacy-rates-ltr">{compactResult(r.resultJson)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
